import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../../lib/api";
import { checkDomain, checkEntityDomain, type DomainCheck } from "../../../../lib/domainCheck";

/** Idle -> busy -> done/error. Idle exists because the check is never automatic. */
export type DomainCheckState =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "done"; check: DomainCheck }
  | { kind: "error"; messageKey: string };

/** The error codes both endpoints answer with, mapped to what the operator reads. */
function messageKeyFor(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string }) : null;
  if (body?.error === "invalid_domain") return "ariregister.dns.invalid";
  if (body?.error === "no_website") return "ariregister.dns.noWebsite";
  return "ariregister.dns.failed";
}

/**
 * The state machine both hooks below share. Checking a register entity and
 * checking a bare address differ in the request and in nothing else, so the
 * request is the only thing passed in.
 */
function useDomainCheckState(fetcher: (refresh: boolean) => Promise<DomainCheck>, autoRun: boolean) {
  const [state, setState] = useState<DomainCheckState>({ kind: "idle" });

  const run = useCallback(
    async (refresh = false) => {
      setState({ kind: "busy" });
      try {
        setState({ kind: "done", check: await fetcher(refresh) });
      } catch (err) {
        setState({ kind: "error", messageKey: messageKeyFor(err) });
      }
    },
    [fetcher],
  );

  useEffect(() => {
    if (autoRun) void run(false);
  }, [autoRun, run]);

  return { state, run };
}

/**
 * Runs the domain check for one register entity.
 *
 * `autoRun` is for surfaces that exist only to show the check (the list-row
 * modal); the detail page leaves it off so opening a company does not fire a
 * registry query nobody asked for.
 */
export function useDomainCheck(regCode: string, autoRun = false) {
  const fetcher = useCallback((refresh: boolean) => checkEntityDomain(regCode, refresh), [regCode]);
  return useDomainCheckState(fetcher, autoRun);
}

/**
 * The same check for a bare address, where there is no register entity to hang
 * it on: the site scanner is handed a domain and nothing else.
 */
export function useDomainLookup(domain: string, autoRun = false) {
  const fetcher = useCallback((refresh: boolean) => checkDomain(domain, refresh), [domain]);
  return useDomainCheckState(fetcher, autoRun);
}
